import React, { useContext, useState } from "react";
import { motion } from "framer-motion";
import BaseButton from "components/base-button/base-button";
import BaseInput from "components/base-input/base-input";
import useModal from "hooks/use-modal";
import { MainStore } from "../../stores/Main";
import API from "../../utils/api";
import { AlertTriangle, Lock, UserX, Shield } from "lucide-react";

const FormAccount = ({ ...props }) => {
  // Unlink alert states
  const ALERTSTATE_OFF = "off";
  const ALERTSTATE_CHOICE = "choice";
  const ALERTSTATE_ALL = "all";
  const ALERTSTATE_AGENCY = "agency";

  const mainStore = useContext(MainStore);
  const { closeModal } = useModal();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const DEFAULT_STATE = mainStore.agency ? ALERTSTATE_CHOICE : ALERTSTATE_ALL;
  const [unregAlert, setUnregAlert] = useState(ALERTSTATE_OFF);

  const [resetForm, setResetForm] = useState({
    current: "",
    pw: "",
    conf: "",
  });

  const itemAnimation = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  const resetPassword = async () => {
    if (resetForm.pw === resetForm.conf) {
      setSaving(true);
      try {
        const response = await API.post("/change-password", {
          Password: resetForm.pw,
          CurPassword: resetForm.current,
        });
        if (response.data.result_code === 101) {
          setError("Invalid password. Please try again.");
        } else if (response.data.result_code === 102) {
          setError("Required: 8 characters, 1 number, 1 special");
        } else {
          closeModal();
        }
        setResetForm({ ...resetForm, pw: "", conf: "" });
        setSaving(false);
      } catch (e) {
        setSaving(false);
      }
    }
  };

  const unregisterConfirm = async () => {
    try {
      setSaving(true);
      const response = await API.post(
        "/profile/disconnect/" + unregAlert,
        null
      );
      if (response.data.result_code === 101) {
        setError(
          "Unable to disconnect your account. Please try again or contact us."
        );
      } else {
        mainStore.handleLogout();
      }
    } catch (e) {
      setSaving(false);
    }
  };

  if (unregAlert !== ALERTSTATE_OFF) {
    switch (unregAlert) {
      case ALERTSTATE_CHOICE:
        return (
          <motion.div
            variants={itemAnimation}
            initial="hidden"
            animate="visible"
            className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-gray-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <UserX className="w-5 h-5 text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Disconnect Options
              </h3>
            </div>
            
            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              You can disconnect your Freede profile from this agency or you can
              disconnect all agencies and close your Freede profile. Please make
              your selection below.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setUnregAlert(ALERTSTATE_AGENCY)}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors font-medium"
              >
                This Agency Only
              </button>
              <button
                onClick={() => setUnregAlert(ALERTSTATE_ALL)}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg transition-colors font-medium"
              >
                All Agencies and Freede
              </button>
            </div>
          </motion.div>
        );

      case ALERTSTATE_AGENCY:
        return (
          <motion.div
            variants={itemAnimation}
            initial="hidden"
            animate="visible"
            className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-yellow-600/30"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Disconnect from Agency
              </h3>
            </div>

            <div className="space-y-3 text-sm text-gray-300 leading-relaxed mb-6">
              <p>
                You are disconnecting your access to view and manage your accounts
                with this agency. If you proceed and disconnect this agency, you
                will lose access to your account information through this platform
                and {mainStore.profile.agency.name} is notified that you do not
                wish to manage your accounts using the Freede platform.{" "}
                {mainStore.profile.agency.name} may choose to contact you another
                way to resolve these accounts.
              </p>
              <p>
                Please be aware that when you disconnect your access to view and
                manage your accounts with {mainStore.profile.agency.name}, Freede
                will cancel any future scheduled payments intended to be remitted
                to {mainStore.profile.agency.name} through this platform that have
                not already been initiated, however, your commitments to remit
                payment for any payment arrangement negotiated through the Freede
                platform or with {mainStore.profile.agency.name} or any other
                party are still in effect. Payment remittance instructions can be
                obtained by contacting {mainStore.profile.agency.name} at{" "}
                {mainStore.profile.agency.phone_toll} during regular business
                hours ({mainStore.profile.agency.hours}).
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => closeModal()}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => unregisterConfirm()}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded-lg transition-colors font-medium"
              >
                Proceed
              </button>
            </div>
          </motion.div>
        );

      case ALERTSTATE_ALL:
        return (
          <motion.div
            variants={itemAnimation}
            initial="hidden"
            animate="visible"
            className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-red-600/30"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Disconnect from All Agencies and Close Freede Profile
              </h3>
            </div>

            <div className="space-y-3 text-sm text-gray-300 leading-relaxed mb-6">
              <p>
                You are requesting to close your Freede profile. This will
                disconnect your profile from all linked agencies and immediately
                close your Freede profile.
              </p>
              <p>
                Please be aware that when you disconnect your account, Freede will
                cancel any future scheduled payments through this platform that
                have not already been initiated, however, your commitments to
                remit payment for any payment arrangement negotiated through the
                Freede platform or with any agencies or any other party are still
                in effect. Payment remittance instructions can be obtained by
                contacting your agencies directly during regular business hours.
              </p>
              <p>
                When you submit a request to close your Freede profile, you will
                immediately lose all access to the information currently available
                to you when you are logged in to the Freede platform. Your profile
                and linked data can not be restored. Additionally, your request to
                close your Freede profile will disconnect any social media or
                financial accounts linked to your Freede profile. Freede will
                manage any data associated with your Freede profile in accordance
                with the Terms and Conditions and Privacy Policy of the Freede
                platform or as otherwise required by law.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => closeModal()}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => unregisterConfirm()}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded-lg transition-colors font-medium"
              >
                Proceed
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  } else {
    return (
      <motion.div
        variants={itemAnimation}
        initial="hidden"
        animate="visible"
      >
        {/* Password Change Card */}
        <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-gray-700/50 mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Current Password
              </label>
              <BaseInput
                id="acc-pass"
                placeholder="Enter current password"
                type="password"
                disabled={saving}
                onChange={(e) =>
                  setResetForm({ ...resetForm, current: e.target.value })
                }
                className="w-full bg-gray-900/50 border-gray-700 text-white placeholder-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                New Password
              </label>
              <BaseInput
                id="acc-pass-new"
                placeholder="Enter new password"
                type="password"
                disabled={saving}
                value={resetForm.pw}
                error={resetForm.pw !== resetForm.conf}
                onChange={(e) =>
                  setResetForm({ ...resetForm, pw: e.target.value })
                }
                className="w-full bg-gray-900/50 border-gray-700 text-white placeholder-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Confirm New Password
              </label>
              <BaseInput
                id="acc-pass-new-confirm"
                placeholder="Confirm new password"
                type="password"
                disabled={saving}
                value={resetForm.conf}
                error={resetForm.pw !== resetForm.conf}
                onChange={(e) =>
                  setResetForm({ ...resetForm, conf: e.target.value })
                }
                className="w-full bg-gray-900/50 border-gray-700 text-white placeholder-gray-500"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-400">{error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setUnregAlert(DEFAULT_STATE)}
            disabled={saving}
            className="px-4 py-2.5 bg-red-600/10 hover:bg-red-600/20 disabled:bg-gray-800 text-red-400 border border-red-600/30 rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
          >
            <UserX className="w-4 h-4" />
            Deregister Account
          </button>
          
          <div className="flex gap-3 flex-1 justify-end">
            <button
              onClick={resetPassword}
              disabled={saving || resetForm.pw !== resetForm.conf || !resetForm.pw}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors font-medium"
            >
              Save Password
            </button>
            <button
              onClick={() => closeModal()}
              disabled={saving}
              className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    );
  }
};

export default FormAccount;